/**
 * Deterministic survival / elimination assignment for The Gauntlet.
 * Counts come strictly from aleData.json pass rates — no sampling noise.
 * Phase 2: false-summit flags, task assignment, null-tier handling (Sol).
 */

import aleData from '../data/aleData.json' with { type: 'json' };
import { BOTS_PER_LANE, PROGRESS } from './gauntletCourse';

export interface LeaderboardEntry {
  agent: string;
  backbone: string;
  /** Full-pass %; null = no reported data for this tier (never invent). */
  nearTerm: number | null;
  fullSpectrum: number | null;
  lastExam: number | null;
  overall: number | null;
  meanScoreNearTerm?: number | null;
  meanScoreFullSpectrum?: number | null;
  meanScoreLastExam?: number | null;
  costTotalUsd?: number | null;
  costPerTaskUsd?: number | null;
  runtimeHours?: number | null;
  color?: string | null;
  verified: boolean;
  source?: string;
  note?: string;
  /** Gold-trim Sol challenger treatment. */
  isSol?: boolean;
  /** Press-reported caveat badge. */
  pressReported?: boolean;
}

export type BotStatus =
  | 'waiting'
  | 'running'
  | 'false_summit'
  | 'false_summit_flip'
  | 'eliminating'
  | 'eliminated'
  | 'clinging'
  | 'done'
  /** Tier rate is null — bot idles at the gate with a "no reported data" plaque. */
  | 'idle_no_data';

export interface ExampleTask {
  id: string;
  title: string;
  domain: string;
  software: string;
  tier: string | null;
  description: string;
  humanTime: string | null;
  source: string;
}

export interface BotSim {
  /** Assigned fail stage: 0=gate1, 1=gate2, 2=cliff, 3=cling (no fail), 4=no-data idle */
  fate: 0 | 1 | 2 | 3 | 4;
  /** Progress along course where elimination/cling/false-summit triggers */
  failAt: number;
  /** Per-instance locomotion jitter */
  phase: number;
  speedJitter: number;
  status: BotStatus;
  progress: number;
  elimT: number;
  /** False-summit celebrate / flip timer (seconds). */
  fsT: number;
  isFalseSummit: boolean;
  startX: number;
  startY: number;
  startZ: number;
  pitX: number;
  pitY: number;
  pitZ: number;
  /** Index into EXAMPLE_TASKS (assigned at spawn). */
  taskIndex: number;
  /** Physics tumbler spawned for this bot. */
  physicsSpawned: boolean;
}

export const EXAMPLE_TASKS = aleData.exampleTasks as ExampleTask[];

/** Prefer cheetah task in the rotation; weight by tier affinity to bot fate. */
const TIER_WEIGHT: Record<string, number> = {
  'near-term': 0,
  'full-spectrum': 1,
  'last-exam': 2,
};

export function laneId(entry: LeaderboardEntry): string {
  return `${entry.agent}|${entry.backbone}`;
}

/** Mulberry32 PRNG — deterministic from seed. */
export function makeRng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function shuffleIndices(n: number, rng: () => number): number[] {
  const arr = new Array<number>(n);
  for (let i = 0; i < n; i += 1) arr[i] = i;
  for (let i = n - 1; i > 0; i -= 1) {
    const j = (rng() * (i + 1)) | 0;
    const tmp = arr[i];
    arr[i] = arr[j];
    arr[j] = tmp;
  }
  return arr;
}

/**
 * Survival counts of 100 bots from tier pass rates (%).
 * Null tier → no survivors past that gate (and no invented rate);
 * caller assigns idle_no_data rather than elim counts when the first null is hit.
 */
export function survivalCounts(entry: LeaderboardEntry): {
  afterGate1: number;
  afterGate2: number;
  clinging: number;
  elimGate1: number;
  elimGate2: number;
  elimCliff: number;
  /** First tier with null rate (0/1/2), or -1 if all present. */
  nullTierFrom: number;
} {
  const nt = entry.nearTerm;
  const fs = entry.fullSpectrum;
  const le = entry.lastExam;

  // If near-term is missing, nothing advances — all idle at gate 1.
  if (nt == null) {
    return {
      afterGate1: 0,
      afterGate2: 0,
      clinging: 0,
      elimGate1: 0,
      elimGate2: 0,
      elimCliff: 0,
      nullTierFrom: 0,
    };
  }

  const afterGate1 = Math.min(BOTS_PER_LANE, Math.max(0, Math.round(nt)));

  if (fs == null) {
    return {
      afterGate1,
      afterGate2: 0,
      clinging: 0,
      elimGate1: BOTS_PER_LANE - afterGate1,
      elimGate2: 0,
      elimCliff: 0,
      nullTierFrom: 1,
    };
  }

  const afterGate2 = Math.min(afterGate1, Math.max(0, Math.round(fs)));

  if (le == null) {
    return {
      afterGate1,
      afterGate2,
      clinging: 0,
      elimGate1: BOTS_PER_LANE - afterGate1,
      elimGate2: afterGate1 - afterGate2,
      elimCliff: 0,
      nullTierFrom: 2,
    };
  }

  const clinging = Math.min(afterGate2, Math.max(0, Math.round(le)));
  return {
    afterGate1,
    afterGate2,
    clinging,
    elimGate1: BOTS_PER_LANE - afterGate1,
    elimGate2: afterGate1 - afterGate2,
    elimCliff: afterGate2 - clinging,
    nullTierFrom: -1,
  };
}

/** Fraction of gate failers that get the false-summit treatment. */
const FALSE_SUMMIT_FRAC = 0.18;

function pickTaskIndex(fate: number, botIndex: number, rng: () => number): number {
  // Always keep cheetah (index 0) in rotation for some bots.
  if (botIndex % 17 === 0) return 0;

  const preferTier =
    fate === 0 ? ['near-term', null] : fate === 1 ? ['full-spectrum', null] : ['last-exam', null];

  const weighted: number[] = [];
  for (let i = 0; i < EXAMPLE_TASKS.length; i += 1) {
    const t = EXAMPLE_TASKS[i];
    const tierKey = t.tier;
    let w = 1;
    if (tierKey && preferTier.includes(tierKey)) w = 4;
    else if (tierKey == null) w = 2;
    // slight bias for matching domain-ish fate
    if (fate >= 2 && TIER_WEIGHT[tierKey ?? ''] === 2) w += 2;
    for (let k = 0; k < w; k += 1) weighted.push(i);
  }
  return weighted[(rng() * weighted.length) | 0] ?? 0;
}

/**
 * Assign each of 100 bots a fate + fail progress. Deterministic for (entry, seed).
 * WHICH bots fail is shuffled; counts match round(rate) exactly when rates exist.
 */
export function assignFates(entry: LeaderboardEntry, seed: number): BotSim[] {
  const rng = makeRng(seed ^ hashStr(laneId(entry)));
  const counts = survivalCounts(entry);
  const order = shuffleIndices(BOTS_PER_LANE, rng);

  const bots: BotSim[] = new Array(BOTS_PER_LANE);
  for (let i = 0; i < BOTS_PER_LANE; i += 1) {
    bots[i] = {
      fate: 2,
      failAt: PROGRESS.stage3End,
      phase: rng() * Math.PI * 2,
      speedJitter: 0.85 + rng() * 0.3,
      status: 'waiting',
      progress: 0,
      elimT: 0,
      fsT: 0,
      isFalseSummit: false,
      startX: 0,
      startY: 0,
      startZ: 0,
      pitX: 0,
      pitY: 0,
      pitZ: 0,
      taskIndex: 0,
      physicsSpawned: false,
    };
  }

  // All bots idle with no data when near-term is null (Sol incomplete tiers).
  if (counts.nullTierFrom === 0) {
    for (let i = 0; i < BOTS_PER_LANE; i += 1) {
      const bot = bots[i];
      bot.fate = 4;
      bot.failAt = PROGRESS.gate1 - 0.02;
      bot.taskIndex = pickTaskIndex(0, i, rng);
    }
    return bots;
  }

  let cursor = 0;

  // Gate 1 failers — slice as false summits mid-stage
  const fs1 = Math.max(1, Math.round(counts.elimGate1 * FALSE_SUMMIT_FRAC));
  for (let k = 0; k < counts.elimGate1; k += 1) {
    const idx = order[cursor++];
    const bot = bots[idx];
    bot.fate = 0;
    bot.isFalseSummit = k < fs1;
    if (bot.isFalseSummit) {
      // Mid stage-1 stop
      bot.failAt = PROGRESS.stage1Start + 0.08 + (k / Math.max(1, fs1)) * 0.16;
    } else {
      bot.failAt = PROGRESS.gate1 - 0.012 + (k / Math.max(1, counts.elimGate1)) * 0.02;
    }
    bot.taskIndex = pickTaskIndex(0, idx, rng);
  }

  // Survived gate 1 but full-spectrum is null → idle at gate 2 approach
  if (counts.nullTierFrom === 1) {
    for (let k = 0; k < counts.afterGate1; k += 1) {
      const idx = order[cursor++];
      const bot = bots[idx];
      bot.fate = 4;
      bot.failAt = PROGRESS.gate2 - 0.04;
      bot.taskIndex = pickTaskIndex(1, idx, rng);
    }
    return bots;
  }

  // Gate 2 failers
  const fs2 = Math.max(0, Math.round(counts.elimGate2 * FALSE_SUMMIT_FRAC));
  for (let k = 0; k < counts.elimGate2; k += 1) {
    const idx = order[cursor++];
    const bot = bots[idx];
    bot.fate = 1;
    bot.isFalseSummit = k < fs2;
    if (bot.isFalseSummit) {
      bot.failAt = PROGRESS.stage2Start + 0.06 + (k / Math.max(1, Math.max(fs2, 1))) * 0.18;
    } else {
      bot.failAt = PROGRESS.gate2 - 0.012 + (k / Math.max(1, counts.elimGate2)) * 0.02;
    }
    bot.taskIndex = pickTaskIndex(1, idx, rng);
  }

  if (counts.nullTierFrom === 2) {
    for (let k = 0; k < counts.afterGate2; k += 1) {
      const idx = order[cursor++];
      const bot = bots[idx];
      bot.fate = 4;
      bot.failAt = PROGRESS.stage3Start + 0.02;
      bot.taskIndex = pickTaskIndex(2, idx, rng);
    }
    return bots;
  }

  // Cliff failers
  for (let k = 0; k < counts.elimCliff; k += 1) {
    const idx = order[cursor++];
    const bot = bots[idx];
    bot.fate = 2;
    const climbFrac = 0.08 + rng() * 0.78;
    bot.failAt =
      PROGRESS.stage3Start + climbFrac * (PROGRESS.stage3End - PROGRESS.stage3Start);
    bot.taskIndex = pickTaskIndex(2, idx, rng);
  }

  // Clingers
  for (let k = 0; k < counts.clinging; k += 1) {
    const idx = order[cursor++];
    const bot = bots[idx];
    bot.fate = 3;
    bot.failAt = PROGRESS.cliffLedge + (rng() - 0.5) * 0.03;
    bot.taskIndex = pickTaskIndex(3, idx, rng);
  }

  // Safety: any leftover indices get cliff fail
  while (cursor < BOTS_PER_LANE) {
    const idx = order[cursor++];
    const bot = bots[idx];
    bot.fate = 2;
    bot.failAt = PROGRESS.stage3Start + 0.2;
    bot.taskIndex = pickTaskIndex(2, idx, rng);
  }

  return bots;
}

function hashStr(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Base hop-jog progress rate at 1× (full course in ~22s for median bot). */
export const BASE_PROGRESS_RATE = 1 / 22;

/** False-summit phase durations (seconds at 1×). */
export const FS_CELEBRATE_SEC = 0.75;
export const FS_FLIP_SEC = 0.55;

/** Synthetic Sol challenger entry — only reported overall; tiers null (no invention). */
export function createSolEntry(): LeaderboardEntry {
  const sol = aleData.gpt56Sol as {
    reportedScores: { aleCodeModePassPct: number | null };
    verified: boolean;
    sources: string[];
    caveat: string;
  };
  return {
    agent: 'GPT-5.6 Sol',
    backbone: 'code mode',
    nearTerm: null,
    fullSpectrum: null,
    lastExam: null,
    overall: sol.reportedScores.aleCodeModePassPct,
    verified: false,
    pressReported: true,
    isSol: true,
    source: sol.sources[0],
    note: sol.caveat,
  };
}

export const SOL_LANE_ID = laneId(createSolEntry());

/** Terminal-Bench pass rate for gotcha side-course (from contrast, not invented). */
export function terminalBenchRateFor(entry: LeaderboardEntry): number {
  const tb = aleData.contrast.terminalBench as { score: number; model: string };
  const sol = aleData.gpt56Sol as {
    reportedScores: { terminalBench21StandardPct: number | null };
  };
  if (entry.isSol && sol.reportedScores.terminalBench21StandardPct != null) {
    return sol.reportedScores.terminalBench21StandardPct;
  }
  // contrast lists Codex + GPT-5.5 at 82%; use that when it matches, else same score as demo contrast
  return tb.score;
}

/**
 * Assign fates for a short Terminal-Bench side course: single gate, pass rate = TB score.
 * Survivors reach "done" at the end; failers eliminate at the one gate.
 */
export function assignTerminalBenchFates(entry: LeaderboardEntry, seed: number): BotSim[] {
  const rate = terminalBenchRateFor(entry);
  const survivors = Math.min(BOTS_PER_LANE, Math.max(0, Math.round(rate)));
  const elim = BOTS_PER_LANE - survivors;
  const rng = makeRng(seed ^ 0x7b11 ^ hashStr(laneId(entry)));
  const order = shuffleIndices(BOTS_PER_LANE, rng);

  const bots: BotSim[] = new Array(BOTS_PER_LANE);
  for (let i = 0; i < BOTS_PER_LANE; i += 1) {
    bots[i] = {
      fate: 3,
      failAt: 0.92,
      phase: rng() * Math.PI * 2,
      speedJitter: 0.9 + rng() * 0.2,
      status: 'waiting',
      progress: 0,
      elimT: 0,
      fsT: 0,
      isFalseSummit: false,
      startX: 0,
      startY: 0,
      startZ: 0,
      pitX: 0,
      pitY: 0,
      pitZ: 0,
      taskIndex: pickTaskIndex(0, i, rng),
      physicsSpawned: false,
    };
  }

  let cursor = 0;
  for (let k = 0; k < elim; k += 1) {
    const idx = order[cursor++];
    const bot = bots[idx];
    bot.fate = 0;
    bot.failAt = 0.55 + (k / Math.max(1, elim)) * 0.08;
  }
  for (let k = 0; k < survivors; k += 1) {
    const idx = order[cursor++];
    const bot = bots[idx];
    bot.fate = 3;
    bot.failAt = 0.95;
  }
  return bots;
}
